import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Film, AlertCircle, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { streamFlowApi } from '@lib/streamFlowApi';
import type { Provider, LibraryItem } from '@lib/streamFlowApi';
import { supabase } from '@lib/supabase';
import { getApiBaseUrl, getApiBaseMode } from '@lib/apiBase';
import HLSPlayer from '@components/streamflow/HLSPlayer';

// Posters live behind an authenticated endpoint, so <img src> can't
// load them directly (the browser won't attach the Bearer header). We
// fetch each one as a blob with the token and surface an object URL,
// then revoke it on unmount to avoid leaking memory on long library
// scrolls.
function usePosterBlob(url: string | null): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!url) {
      setSrc(null);
      return;
    }
    let revoked = false;
    let createdUrl: string | null = null;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;
      const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
      if (getApiBaseMode() === 'tailnet') headers['X-Client-On-Tailnet'] = '1';
      try {
        const res = await fetch(url, { headers });
        if (!res.ok) return;
        const blob = await res.blob();
        if (revoked) return;
        createdUrl = URL.createObjectURL(blob);
        setSrc(createdUrl);
      } catch {
        /* poster failed; card falls back to placeholder */
      }
    })();
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [url]);
  return src;
}

function PosterImage({ url, alt }: { url: string; alt: string }) {
  // The server returns full URLs that already point at the chosen API
  // base (because the poster URL is built from the inbound request host).
  // When the SPA is in tailnet mode but the server saw the public host,
  // we'd hit a CORS/wrong-base mismatch — defend by rewriting to the
  // current api base if the path matches our shape.
  const rewritten = rewriteToCurrentApiBase(url);
  const src = usePosterBlob(rewritten);
  if (!src) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <Film className="w-8 h-8 text-gray-300" />
      </div>
    );
  }
  return <img src={src} alt={alt} className="w-full h-full object-cover" />;
}

function rewriteToCurrentApiBase(url: string): string {
  const apiBase = getApiBaseUrl();
  const match = url.match(/\/api\/providers\/.+/);
  if (!match) return url;
  return `${apiBase.replace(/\/api$/, '')}${match[0]}`;
}

export default function Library() {
  const navigate = useNavigate();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [playUrl, setPlayUrl] = useState<string | null>(null);
  const [playingItem, setPlayingItem] = useState<LibraryItem | null>(null);
  const [playError, setPlayError] = useState<string | null>(null);
  const playRequestRef = useRef(0);

  // Load Jellyfin providers visible to the household, then auto-select
  // the first one so the grid populates without an extra click.
  useEffect(() => {
    let cancelled = false;
    streamFlowApi.listProviders().then((res) => {
      if (cancelled) return;
      if (res.success && res.data) {
        const jelly = res.data.filter((p) => p.provider_type === 'jellyfin');
        setProviders(jelly);
        if (jelly.length > 0) setSelectedProviderId(jelly[0]!.id);
      }
      setLoadingProviders(false);
    });
    return () => { cancelled = true; };
  }, []);

  // Refetch items every time the selected provider changes.
  useEffect(() => {
    if (!selectedProviderId) {
      setItems([]);
      return;
    }
    let cancelled = false;
    setLoadingItems(true);
    setItemsError(null);
    streamFlowApi.listLibraryItems(selectedProviderId).then((res) => {
      if (cancelled) return;
      if (res.success && res.data) {
        setItems(res.data.items);
      } else {
        setItems([]);
        setItemsError(res.error?.message ?? 'Failed to load library');
      }
      setLoadingItems(false);
    });
    return () => { cancelled = true; };
  }, [selectedProviderId]);

  const handlePlay = useCallback(async (item: LibraryItem) => {
    if (!selectedProviderId) return;
    const seq = ++playRequestRef.current;
    setPlayError(null);
    setPlayingItem(item);
    setPlayUrl(null);
    const res = await streamFlowApi.playLibraryItem(selectedProviderId, item.id);
    if (playRequestRef.current !== seq) return;
    if (res.success && res.data) {
      setPlayUrl(res.data.stream_url);
    } else {
      setPlayError(res.error?.message ?? 'Failed to start playback');
      toast.error(res.error?.message ?? 'Failed to start playback');
    }
  }, [selectedProviderId]);

  const closePlayer = useCallback(() => {
    setPlayingItem(null);
    setPlayUrl(null);
    setPlayError(null);
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </button>

      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Film className="w-6 h-6" />
            Library
          </h1>
          <p className="text-sm text-gray-500">Movies and series from your Jellyfin servers</p>
        </div>
        {providers.length > 0 && (
          <select
            value={selectedProviderId ?? ''}
            onChange={(e) => setSelectedProviderId(e.target.value || null)}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
          >
            {providers.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        )}
      </div>

      {loadingProviders ? (
        <div className="text-center py-12 text-gray-500">Loading providers...</div>
      ) : providers.length === 0 ? (
        <div className="text-center py-16 bg-white border rounded-lg">
          <Film className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Jellyfin servers registered</h3>
          <p className="text-gray-500 mb-4 max-w-md mx-auto">
            Add a Jellyfin provider to browse your home library here.
          </p>
          <button
            onClick={() => navigate('/providers')}
            className="px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700"
          >
            Add Provider
          </button>
        </div>
      ) : loadingItems ? (
        <div className="text-center py-12 text-gray-500">Loading library...</div>
      ) : itemsError ? (
        <div className="text-center py-12 text-gray-500">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-red-400" />
          <p className="text-sm">{itemsError}</p>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No items found. Make sure Jellyfin is reachable and the library is populated.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handlePlay(item)}
              className="group border rounded-lg p-3 text-left hover:border-slate-500 hover:shadow-md transition-all"
            >
              <div className="w-full aspect-[2/3] bg-gray-100 rounded mb-2 overflow-hidden">
                <PosterImage url={item.poster_url} alt={item.name} />
              </div>
              <p className="text-xs font-medium truncate">{item.name}</p>
              <p className="text-xs text-gray-400 truncate">
                {item.type}{item.year ? ` · ${item.year}` : ''}
              </p>
            </button>
          ))}
        </div>
      )}

      {playingItem && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-5xl">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-white text-lg font-semibold">{playingItem.name}</h2>
                {playingItem.year && (
                  <p className="text-white/60 text-sm">{playingItem.year}</p>
                )}
              </div>
              <button
                onClick={closePlayer}
                className="p-2 text-white hover:bg-white/10 rounded-lg"
                aria-label="Close player"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {playError ? (
              <div className="w-full aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
                <div className="text-center text-white">
                  <AlertCircle className="w-10 h-10 mx-auto mb-3 text-red-400" />
                  <p className="text-sm">{playError}</p>
                </div>
              </div>
            ) : playUrl ? (
              <HLSPlayer
                url={playUrl}
                channelName={playingItem.name}
                onError={(err) => {
                  if (err.type === 'fatal') {
                    setPlayError(err.message);
                  } else {
                    toast.error(err.message);
                  }
                }}
                onBack={closePlayer}
              />
            ) : (
              <div className="w-full aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
                <p className="text-white text-sm">Loading stream...</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
